/**
 * OfflineIndicator - Indicateur visuel du statut offline/online
 *
 * Affiche :
 * - Le statut de connexion (online/offline)
 * - L'identifiant du POS (Hardware Bridge)
 * - Le nombre de ventes en attente de sync
 * - Les erreurs/conflits de sync
 * - La dernière sync du catalogue
 */
import { useState, useEffect } from 'react';
import {
  Wifi,
  WifiOff,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  CloudOff,
  Database,
  ChevronDown,
  ChevronUp,
  X,
  Monitor,
  ArrowRightLeft,
} from 'lucide-react';
import { useOffline } from '../../contexts/OfflineContext';
import { PendingSale } from '../../lib/offline';
import { posApi } from '../../api/pos.api';
import { logger } from '../../utils/logger';
import { formatQty } from '../../utils/pos.utils';

// Nettoie le bruit de virgule flottante dans un message backend
// (ex. « Disponible: 0.19099999999999799 » → « Disponible: 0.191 »).
const cleanFloatNoise = (msg: string): string =>
  msg.replace(/\d+\.\d{5,}/g, (m) => formatQty(m));

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

export function OfflineIndicator() {
  const {
    isOnline,
    isSyncingCatalog,
    isSyncingSales,
    pendingSalesCount,
    conflictSalesCount,
    errorSalesCount,
    offlineProductsCount,
    lastCatalogSync,
    catalogNeedsSync,
    syncCatalog,
    syncSales,
    getConflictSales,
    getErrorSales,
    forceFullSale,
    retrySale,
    abandonSale,
  } = useOffline();

  const [isExpanded, setIsExpanded] = useState(false);
  const [conflictSales, setConflictSales] = useState<PendingSale[]>([]);
  const [errorSales, setErrorSales] = useState<PendingSale[]>([]);
  const [posId, setPosId] = useState<string | null>(null);
  const [hardwareBridgeConnected, setHardwareBridgeConnected] = useState(false);

  // Récupérer le POS ID depuis le Hardware Bridge
  useEffect(() => {
    const fetchPosId = async () => {
      try {
        const urls = ['https://localhost:3334/health'];

        for (const url of urls) {
          try {
            const response = await fetch(url, {
              method: 'GET',
              signal: AbortSignal.timeout(2000),
            });

            if (response.ok) {
              const data = await response.json();
              setPosId(data.posId || null);
              setHardwareBridgeConnected(true);
              // Sauvegarder dans localStorage pour les logs de ventes
              if (data.posId) {
                localStorage.setItem('pos_id', data.posId);
              }
              logger.log('[OfflineIndicator] Hardware Bridge connecté, POS ID:', data.posId);
              return;
            }
          } catch {
            // Continuer avec l'URL suivante
          }
        }

        // Aucune connexion réussie
        setHardwareBridgeConnected(false);
        setPosId(localStorage.getItem('pos_id'));
      } catch (err) {
        logger.warn('[OfflineIndicator] Hardware Bridge non disponible');
        setHardwareBridgeConnected(false);
      }
    };

    fetchPosId();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(fetchPosId, 30000);
    return () => clearInterval(interval);
  }, []);

  const hasIssues = conflictSalesCount > 0 || errorSalesCount > 0;
  const hasPending = pendingSalesCount > 0;
  const isSyncing = isSyncingCatalog || isSyncingSales;

  // Charger les détails des ventes problématiques
  const loadIssueDetails = async () => {
    const [conflicts, errors] = await Promise.all([
      getConflictSales(),
      getErrorSales(),
    ]);
    setConflictSales(conflicts);
    setErrorSales(errors);
  };

  const handleExpand = () => {
    if (!isExpanded) {
      loadIssueDetails();
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="relative">
      {/* Barre compacte */}
      <div
        className={`flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
          !isOnline
            ? 'bg-red-100 dark:bg-red-900/30'
            : hasIssues
            ? 'bg-amber-100 dark:bg-amber-900/30'
            : 'bg-gray-100 dark:bg-gray-800'
        }`}
        onClick={handleExpand}
      >
        {/* POS ID */}
        {posId && (
          <>
            <div className={`flex items-center gap-1.5 ${hardwareBridgeConnected ? 'text-blue-600' : 'text-gray-500'}`}>
              <Monitor className="w-4 h-4" />
              <span className="text-xs font-mono font-medium">{posId}</span>
            </div>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
          </>
        )}

        {/* Statut connexion */}
        <div className={`flex items-center gap-2 ${isOnline ? 'text-green-600' : 'text-red-600'}`}>
          {isOnline ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
          <span className="text-sm font-medium hidden sm:inline">
            {isOnline ? 'En ligne' : 'Hors ligne'}
          </span>
        </div>

        {/* Séparateur */}
        <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />

        {/* Produits offline */}
        {offlineProductsCount > 0 && (
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <Database className="w-4 h-4" />
            <span className="text-xs">{offlineProductsCount}</span>
          </div>
        )}

        {/* Ventes en attente */}
        {hasPending && (
          <div className="flex items-center gap-2 text-amber-600">
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            <span className="text-sm">
              {pendingSalesCount} en attente
            </span>
          </div>
        )}

        {/* Erreurs/Conflits */}
        {hasIssues && (
          <div className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm">
              {conflictSalesCount + errorSalesCount} problème{conflictSalesCount + errorSalesCount > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Tout synchronisé */}
        {isOnline && !hasPending && !hasIssues && offlineProductsCount > 0 && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Synchronisé</span>
          </div>
        )}

        {/* Mode offline sans données */}
        {!isOnline && offlineProductsCount === 0 && (
          <div className="flex items-center gap-2 text-red-600">
            <CloudOff className="w-4 h-4" />
            <span className="text-sm">Aucune donnée locale</span>
          </div>
        )}

        {/* Dernière sync */}
        {lastCatalogSync && (
          <div className="ml-auto text-xs text-gray-500 hidden md:block">
            Sync: {formatRelativeTime(lastCatalogSync)}
          </div>
        )}

        {/* Chevron */}
        <div className="ml-2">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Panel détaillé */}
      {isExpanded && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 min-w-[320px]">
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Mode {isOnline ? 'En ligne' : 'Hors ligne'}
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Info POS */}
            {posId && (
              <div className={`flex items-center gap-2 p-2 rounded text-sm ${
                hardwareBridgeConnected
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 dark:bg-gray-900 text-gray-600'
              }`}>
                <Monitor className="w-4 h-4" />
                <span>POS:</span>
                <span className="font-mono font-medium">{posId}</span>
                {hardwareBridgeConnected && (
                  <span className="ml-auto text-xs text-green-600">Bridge OK</span>
                )}
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded">
                <span className="text-gray-600 dark:text-gray-400">Produits en cache</span>
                <span className="font-medium">{offlineProductsCount}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded">
                <span className="text-gray-600 dark:text-gray-400">Ventes en attente</span>
                <span className="font-medium">{pendingSalesCount}</span>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  syncCatalog(true);
                }}
                disabled={!isOnline || isSyncingCatalog}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncingCatalog ? 'animate-spin' : ''}`} />
                {isSyncingCatalog ? 'Sync...' : 'Sync catalogue'}
              </button>

              {hasPending && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    syncSales();
                  }}
                  disabled={!isOnline || isSyncingSales}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${isSyncingSales ? 'animate-spin' : ''}`} />
                  Sync ventes
                </button>
              )}
            </div>

            {/* Liste des conflits */}
            {conflictSales.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-amber-600">Conflits de stock</h4>
                {conflictSales.map((sale) => (
                  <SaleIssueCard
                    key={sale.id}
                    sale={sale}
                    type="conflict"
                    onForceFull={() => {
                      forceFullSale(sale.id).then(loadIssueDetails);
                    }}
                    onAbandon={() => {
                      const motif = window.prompt(
                        'ABANDONNER cette vente ?\n\nElle a été encaissée : l’abandonner signifie que la recette ne sera PAS enregistrée. Son contenu et votre motif seront conservés côté serveur.\n\nMotif (obligatoire) :',
                      );
                      if (motif && motif.trim()) abandonSale(sale.id, motif.trim()).then(loadIssueDetails);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Liste des erreurs */}
            {errorSales.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-red-600">Erreurs de sync</h4>
                {errorSales.map((sale) => (
                  <SaleIssueCard
                    key={sale.id}
                    sale={sale}
                    type="error"
                    onRetry={() => retrySale(sale.id).then(loadIssueDetails)}
                    onAbandon={() => {
                      const motif = window.prompt(
                        'ABANDONNER cette vente ?\n\nElle a été encaissée : l’abandonner signifie que la recette ne sera PAS enregistrée. Son contenu et votre motif seront conservés côté serveur.\n\nMotif (obligatoire) :',
                      );
                      if (motif && motif.trim()) abandonSale(sale.id, motif.trim()).then(loadIssueDetails);
                    }}
                    onForceFull={() => forceFullSale(sale.id).then(loadIssueDetails)}
                    onMoveToDisplay={sale.backroomDetails ? async () => {
                      const bd = sale.backroomDetails!;
                      if (!bd.lot_id) return;
                      try {
                        await posApi.moveToDisplay(bd.lot_id, bd.requested);
                        // Relancer la sync après mise en rayon
                        await retrySale(sale.id);
                        loadIssueDetails();
                      } catch (err: any) {
                        logger.error('[OfflineIndicator] moveToDisplay failed:', err);
                        alert(`Erreur : ${err.message || 'Impossible de mettre en rayon'}`);
                      }
                    } : undefined}
                  />
                ))}
              </div>
            )}

            {/* Alerte si besoin de sync */}
            {catalogNeedsSync && isOnline && (
              <div className="flex items-center gap-2 p-2 text-sm text-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded">
                <AlertTriangle className="w-4 h-4" />
                <span>Le catalogue nécessite une mise à jour</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPOSANT CARTE VENTE EN ERREUR/CONFLIT
// ============================================================================

interface SaleIssueCardProps {
  sale: PendingSale;
  type: 'conflict' | 'error';
  onForceFull?: () => void;
  onRetry?: () => void;
  onAbandon?: () => void;
  onMoveToDisplay?: () => void;
}

function SaleIssueCard({ sale, type, onForceFull, onRetry, onAbandon, onMoveToDisplay }: SaleIssueCardProps) {
  const isConflict = type === 'conflict';
  const isBackroom = !!sale.backroomDetails;

  // NOTE (30/08/2026) : « Abandonner… » n'efface plus rien — la vente est marquée et envoyée
  // au serveur avec son contenu et le motif (table pos_abandoned_sales), et bloque la
  // clôture tant que cet envoi n'a pas abouti. Historique :
  // il n'y avait PLUS de bouton « Annuler » sur la synchronisation des ventes.
  // Une vente en file hors-ligne a TOUJOURS été encaissée (carte/espèces déjà prises) :
  // la jeter = perdre la recette tout en gardant l'argent → réconciliation cassée (incident
  // 26/06/2026 : 68,21 € Bancontact débité mais vente effacée). La seule sortie est de
  // l'ENREGISTRER (force-full / réessayer). Si elle est réellement erronée, on l'enregistre
  // puis on l'annule depuis l'historique des ventes (annulation tracée + stock restauré).

  // Forçage = enregistre la vente même si le stock est insuffisant (oversell assumé).
  // Le stock descend à 0 au maximum, jamais en négatif → confirmation explicite
  const confirmForceFull = (action?: () => void) => {
    if (!action) return;
    const label = sale.lines.filter(l => !l.is_deposit && !l.is_return).map(l => l.product_name).slice(0, 2).join(', ');
    if (window.confirm(`Enregistrer la vente MAINTENANT (${label || 'vente'}, ${(sale.total_cents / 100).toFixed(2)} €) ?\n\nLa vente est enregistrée telle qu'elle a été encaissée et le stock est décompté de toute la quantité vendue, sans jamais descendre sous 0.\n\nSi la récolte ou la réception n'est simplement pas encore encodée, inutile de forcer : encodez-la, et cette vente s'enregistrera d'elle-même dans les 30 secondes.`)) {
      action();
    }
  };
  const borderColor = isConflict
    ? 'border-amber-300 dark:border-amber-700'
    : isBackroom
    ? 'border-orange-300 dark:border-orange-700'
    : 'border-red-300 dark:border-red-700';
  const bgColor = isConflict
    ? 'bg-amber-50 dark:bg-amber-900/10'
    : isBackroom
    ? 'bg-orange-50 dark:bg-orange-900/10'
    : 'bg-red-50 dark:bg-red-900/10';

  // Résumé lisible des produits de la vente
  const productSummary = sale.lines
    .filter(l => !l.is_deposit && !l.is_return)
    .slice(0, 3)
    .map(l => l.product_name)
    .join(', ');
  const extraCount = sale.lines.filter(l => !l.is_deposit && !l.is_return).length - 3;

  return (
    <div className={`p-3 rounded-lg text-sm border-l-4 ${borderColor} ${bgColor}`}>
      {/* En-tête avec résumé produits et montant */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {productSummary}{extraCount > 0 ? ` +${extraCount}` : ''}
          </div>
          <div className="text-gray-500 text-xs">
            {new Date(sale.created_at).toLocaleString('fr-FR')}
          </div>
        </div>
        <div className="text-right ml-2 shrink-0">
          <div className="font-semibold text-gray-900 dark:text-gray-100">
            {(sale.total_cents / 100).toFixed(2)} €
          </div>
          <div className="text-xs text-gray-500">
            {sale.lines.length} article{sale.lines.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Détails backroom — UI dédiée avec action "Mettre en rayon" */}
      {isBackroom && sale.backroomDetails && (
        <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 mb-2">
            <ArrowRightLeft className="w-4 h-4" />
            <span className="font-medium text-sm">Stock en réserve uniquement</span>
          </div>

          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
            {sale.backroomDetails.product_name}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {sale.backroomDetails.backroom_quantity} en réserve — aucun en rayon.
            Transférez le stock en rayon pour valider cette vente.
          </p>

          {onMoveToDisplay && (
            <button
              onClick={onMoveToDisplay}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium bg-orange-500 hover:bg-orange-600 text-white rounded transition-colors"
            >
              <ArrowRightLeft className="w-4 h-4" />
              Mettre {sale.backroomDetails.requested} en rayon et réessayer
            </button>
          )}
        </div>
      )}

      {/* Message d'erreur (sauf backroom qui a son propre affichage) */}
      {sale.errorMessage && !isBackroom && (
        <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-700 dark:text-red-300">
          <strong>Erreur :</strong> {cleanFloatNoise(sale.errorMessage)}
        </div>
      )}

      {/* Vente refusée pour stock : elle est RELANCÉE toute seule toutes les
          30 s, sans forcer. Le message dit ce que le serveur a vu et quand,
          et — pour notre production — la cause presque toujours en jeu : la
          récolte vendue avant d'être encodée (constat du 26/09/2026). Plus de
          « un autre POS a probablement vendu » : c'était presque toujours faux. */}
      {isConflict && sale.conflictDetails && (() => {
        const d = sale.conflictDetails;
        const maison = d.own_production === true || /elouges/i.test(d.product_name || '');
        const vuA = d.checked_at
          ? new Date(d.checked_at).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })
          : null;
        return (
          <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium text-sm">
                {maison ? 'Récolte pas encore encodée ?' : 'Vente en attente de stock'}
              </span>
            </div>

            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {d.product_name}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300">
              Vendu : <strong>{formatQty(d.requested)}</strong> — le serveur ne voyait que{' '}
              <strong>{formatQty(d.available)}</strong> en stock
              {vuA ? ` (à ${vuA})` : ''}.
            </p>

            {maison ? (
              <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">
                Encodez la récolte en réception — <strong>tout ce qui a été récolté, y compris ce qui
                est déjà vendu</strong>. Cette vente s'enregistrera alors d'elle-même.
              </p>
            ) : (
              <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">
                Réception pas encore validée, ou stock à mettre à jour ? Dès que le stock est à jour,
                cette vente s'enregistre d'elle-même.
              </p>
            )}
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Nouvel essai automatique toutes les 30 secondes. Ne comptez pas ce produit avant que
              la vente soit passée : elle serait déduite deux fois.
            </p>
          </div>
        );
      })()}

      {/* Actions avec descriptions */}
      <div className="mt-3 space-y-2">
        {isConflict && (
          <>
            {/* Une vente hors-ligne déjà encaissée ne se renégocie pas : on l'enregistre
                telle quelle (montant encaissé = vérité). Le stock s'arrête à 0, jamais
                négatif. Aucune option de réduction de quantité (sous-déclaration interdite). */}
            {onForceFull && (
              <button
                onClick={() => confirmForceFull(onForceFull)}
                className="w-full flex flex-col items-center justify-center px-3 py-3 text-sm font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <span>Enregistrer maintenant, sans attendre</span>
                <span className="text-xs font-normal opacity-90 mt-0.5">
                  {(sale.total_cents / 100).toFixed(2)} € encaissés · stock décompté (jamais sous 0)
                </span>
              </button>
            )}
          </>
        )}

        {type === 'error' && !isBackroom && (
          <div className="space-y-2">
            {onRetry && (
              <button
                onClick={onRetry}
                className="w-full flex items-center justify-center gap-1 px-3 py-2 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Réessayer
              </button>
            )}
              {onAbandon && (
                <button
                  onClick={onAbandon}
                  className="px-3 py-2 text-xs font-medium text-gray-500 underline underline-offset-2 hover:text-red-700"
                  title="Dernier recours : la vente ne sera pas enregistrée, mais son contenu et le motif sont conservés côté serveur"
                >
                  Abandonner…
                </button>
              )}
            {/* Dernier recours : forcer l'enregistrement malgré le stock */}
            {onForceFull && (
              <button
                onClick={() => confirmForceFull(onForceFull)}
                className="w-full flex items-center justify-center px-3 py-2 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
              >
                Enregistrer la vente complète
              </button>
            )}
          </div>
        )}

        {/* Backroom: forcer l'enregistrement sous l'action principale */}
        {type === 'error' && isBackroom && (
          <div className="space-y-2 mt-1">
            {/* Alternative au transfert en rayon : forcer l'enregistrement direct */}
            {onForceFull && (
              <button
                onClick={() => confirmForceFull(onForceFull)}
                className="w-full flex items-center justify-center px-3 py-2 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
              >
                Enregistrer la vente complète
              </button>
            )}
          </div>
        )}
      </div>

      {/* « Tentatives : n/3 » retiré : le plafond de 3 essais n'existe plus depuis
          le 30/08 (une vente encaissée se réessaie jusqu'à passer). */}
    </div>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "à l'instant";
  if (diffMins < 60) return `il y a ${diffMins} min`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `il y a ${diffHours}h`;

  return date.toLocaleDateString('fr-FR');
}
